import {
  ActionList, ActionListItem, AlertVariant, Badge, Bullseye, Button, Divider, EmptyState,
  EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, MenuToggle, MenuToggleElement,
  PageSection, SearchInput, Select, SelectOption, Split, SplitItem, Switch, Title, Toolbar,
  ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel, ToolbarLabelGroup,
  ToolbarToggleGroup, Tooltip
} from '@patternfly/react-core';
import {
  AngleDownIcon, AngleRightIcon, ExclamationTriangleIcon, FilterIcon, SearchIcon, UsersIcon
} from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

import { InstructorUtilizationApi } from '@app/api';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadError } from '@app/components/DataLoadError';
import { PageActionsMenu, PageActionsMenuItem } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { Instructor } from '@app/model/Instructor';
import {
  FISCAL_MONTH_LABELS, FISCAL_PERIOD_ROWS, InstructorUtilization, UtilizationSeries,
  combineSeries, fiscalYearLabel, fiscalYearMonths, fiscalYearOf, fiscalYearOptions,
  fiscalYearRange, formatDays, hasDelivery, perInstructorAverage, periodFigures, periodMonthSpan,
  periodOverAllocated, toUtilizationSeries, utilizationExportRows
} from '@app/model/Utilization';
import { useInstructorStore, usePartnerStore } from '@app/store/entityStore';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { ExportFormat, exportRecords } from '@app/utils/export';
import { ddMMyyyyFormat, fromIsoDate } from '@app/utils/functions';

/**
 * Instructor utilization: how many days each instructor spent delivering, over one fiscal year.
 *
 * A row per instructor, the twelve months running Apr to Mar with each quarter's total and
 * average beside it, then H1 after Q2, H2 after Q4 and the year last. The foot row is the sum of
 * the rows above it. model/Utilization.ts holds every one of those figures - the endpoint answers
 * one row per instructor and month and nothing wider, so the quarters, halves and year are all
 * arithmetic done here.
 *
 * The page holds no store of its own and starts empty. Every search is a request and the answer
 * is thrown away when the page is left: utilization is derived from classes that the Classes
 * Outlook import restates continuously, so a cached figure is one that has been corrected since.
 */

/** What the toolbar collects. Ids are strings because they come straight out of the controls. */
interface UtilizationCriteria {
  /** The fiscal year, named by the calendar year it starts in. */
  fiscalYear: number;
  instructorId: string[];
  partnerId: string[];
}

const criteriaFor = (fiscalYear: number): UtilizationCriteria =>
  ({ fiscalYear, instructorId: [], partnerId: [] });

/** Whether the search names anybody in particular, as opposed to reporting on everyone. */
const hasSubjectFilter = (criteria: UtilizationCriteria): boolean =>
  criteria.instructorId.length > 0 || criteria.partnerId.length > 0;

/**
 * Who the search covers.
 *
 * **Union, not intersection**: a partner contributes its whole roster and the named instructors
 * are added to it, so one freelancer can be reported alongside a partner's whole team in a single
 * search. An empty result means the filter named nobody who exists - which is not the same as
 * naming nobody at all, and the page is careful to tell those apart.
 */
const resolveInstructorIds = (criteria: UtilizationCriteria, instructors: Instructor[]): number[] => {
  const picked = new Set(criteria.instructorId.map(Number));
  const partners = new Set(criteria.partnerId);

  instructors.forEach(instructor => {
    if (instructor.partner && partners.has(String(instructor.partner.id))) picked.add(instructor.id);
  });

  return [...picked];
};

/** What the Average columns divide by, said once and pointed at from every one of them. */
const AVERAGE_HINT = 'The period total divided by the months the period has - 3 for a quarter, '
  + '6 for a half, 12 for the year - whether or not anything was delivered in them. A month with '
  + 'no delivery counts as a zero.';

/** The label on the first foot row, and on the row the export writes for it. */
const SUM_LABEL = 'Total';

/** Why the average row divides by what it divides by. */
const PER_INSTRUCTOR_HINT = 'The total divided by the instructors who delivered something. '
  + 'Instructors with a year of zeros are left out of the division whether or not they are being '
  + 'shown, so this figure does not move when the switch above is thrown.';

const Utilization: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  // The pickers offer names and the endpoint takes ids, so both dictionaries are needed. The
  // instructor list is also what resolves a partner to its roster, the endpoint having no
  // partner filter of its own.
  const { items: instructors, isLoaded: instructorsLoaded } = useEntity<Instructor>(useInstructorStore);
  const { items: partners } = useEntity(usePartnerStore);

  const years = React.useMemo(() => fiscalYearOptions(), []);
  const currentFiscalYear = React.useMemo(() => fiscalYearOf(new Date()), []);

  // --- Search state ---------------------------------------------------------------------
  /** Being edited in the toolbar. Nothing is requested while this changes. */
  const [draft, setDraft] = React.useState<UtilizationCriteria>(() => criteriaFor(currentFiscalYear));
  /**
   * What was searched for. **Null until the first search**, which is the empty page: nothing is
   * fetched until the reader has chosen a year and whoever they want and pressed Search.
   */
  const [applied, setApplied] = React.useState<UtilizationCriteria | null>(null);

  const [rows, setRows] = React.useState<InstructorUtilization[]>([]);
  const [error, setError] = React.useState<ApiError | null>(null);
  /** A search has answered, so an empty table means "nothing was delivered". */
  const [hasAnswer, setHasAnswer] = React.useState(false);
  /** Bumped to re-run the same query, for Refresh and for Try again. */
  const [refreshToken, setRefreshToken] = React.useState(0);

  // --- View state -----------------------------------------------------------------------
  /**
   * Whether to leave out the instructors who delivered nothing all year.
   *
   * A view switch, not a search criterion: the rows are already in hand, so it takes effect at
   * once and costs no request. Off by default - a year of zeros is a fact about the selection
   * worth seeing once, and the subtitle says how many there are so the switch is offered rather
   * than needed.
   */
  const [hideIdle, setHideIdle] = React.useState(false);
  /**
   * The instructor's row the reader has marked, to keep their place across 27 columns of
   * horizontal scrolling. Clicking it again clears it: the mark is the whole of what a click
   * does here - there is no detail page behind a row - so it has to be undoable by the same
   * gesture that set it.
   */
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(true);
  const [isYearOpen, setIsYearOpen] = React.useState(false);
  const [openFilter, setOpenFilter] = React.useState<string | null>(null);
  const [optionSearch, setOptionSearch] = React.useState('');

  const isFilterOpen = (category: string) => openFilter === category;
  const toggleFilter = (category: string) => {
    setOpenFilter(isFilterOpen(category) ? null : category);
    setOptionSearch('');
  };

  /**
   * Puts the cursor in a filter menu's search box the moment the menu opens, so a name can be
   * typed without a second click.
   *
   * A callback ref rather than an effect keyed on `openFilter`: the menu is rendered through a
   * Popper, so the input does not exist at the point such an effect would run. This fires when
   * the node itself arrives, whether that is on mount or on the render that opens the menu.
   *
   * Attached only to the open category's input - handed to all of them it would grab focus on
   * first paint. Nothing of PatternFly's competes for it: Select leaves
   * `shouldFocusFirstItemOnOpen` false by default.
   */
  const focusOnOpen = React.useCallback((node: HTMLInputElement | null) => node?.focus(), []);

  // --- Fetching -------------------------------------------------------------------------

  /** Who the applied search covers, and whether it could be resolved at all. */
  const subjects = React.useMemo(() => {
    const named = applied ? hasSubjectFilter(applied) : false;
    const ids = named && applied ? resolveInstructorIds(applied, instructors) : [];
    return { named, ids, isEmpty: named && ids.length === 0 };
  }, [applied, instructors]);

  /**
   * A filtered search cannot be made until the instructor list is in: a partner resolves to its
   * roster through it, and an instructor who delivered nothing takes their name from it. An
   * unfiltered search needs none of that and goes straight out.
   */
  const canRequest = !subjects.named || instructorsLoaded;

  /** The last request wins: a slow answer to a superseded query must not land. */
  const requestSeq = React.useRef(0);

  const queryKey = React.useMemo(
    () => JSON.stringify([applied?.fiscalYear ?? null, subjects.ids, subjects.isEmpty, refreshToken]),
    [applied, subjects, refreshToken]
  );
  const fetchedKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!applied) return;
    if (!canRequest) return;
    if (fetchedKey.current === queryKey) return;
    fetchedKey.current = queryKey;

    // The filter named people and none of them exist - a partner with no instructors on it, or
    // an id since removed. Reporting on everybody instead would answer a question nobody asked.
    if (subjects.isEmpty) {
      requestSeq.current++;
      setRows([]);
      setError(null);
      setHasAnswer(true);
      return;
    }

    // Staleness is decided by the sequence number alone, deliberately: dropping the answer in a
    // cleanup would lose it entirely under StrictMode, whose second run is the one the guard
    // above skips.
    const seq = ++requestSeq.current;
    setHasAnswer(false);

    const run = async () => {
      const { from, to } = fiscalYearRange(applied.fiscalYear);
      try {
        const found = await InstructorUtilizationApi.search({
          instructorId: subjects.ids.length > 0 ? subjects.ids : undefined,
          from,
          to
        });
        if (seq !== requestSeq.current) return;
        setRows(found);
        setError(null);
      } catch (failure) {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setError(toApiError(failure));
      } finally {
        if (seq === requestSeq.current) setHasAnswer(true);
      }
    };

    void run();
  }, [queryKey, canRequest, applied, subjects]);

  const search = () => {
    setApplied(draft);
    // The marked row may not be in the next answer at all.
    setSelectedRowId(null);
    // Same criteria as last time still means "search again", which is what the button says.
    setRefreshToken(token => token + 1);
  };

  const refresh = () => {
    if (applied) setRefreshToken(token => token + 1);
  };

  // --- Derived figures --------------------------------------------------------------------

  /**
   * One series per instructor. The resolved ids are passed in so that an instructor who
   * delivered nothing all year still gets a row of zeros - without it, a search for three
   * instructors two of whom were idle answers with one row and looks like a filter that never
   * applied. An unfiltered search passes none, so the page lists whoever delivered rather than
   * every instructor on the books.
   */
  const series = React.useMemo(() => {
    if (!applied) return [];
    const include = subjects.ids.length > 0
      ? instructors.filter(instructor => subjects.ids.includes(instructor.id))
        .map(instructor => ({ id: instructor.id, name: instructor.name }))
      : [];
    return toUtilizationSeries(rows, applied.fiscalYear, include);
  }, [rows, applied, subjects.ids, instructors]);

  /** How many of them delivered nothing at all, whether or not they are being shown. */
  const idleCount = React.useMemo(
    () => series.filter(one => !hasDelivery(one)).length,
    [series]
  );

  /** The rows actually on screen. Everything below counts these and not `series`. */
  const visible = React.useMemo(
    () => (hideIdle ? series.filter(hasDelivery) : series),
    [series, hideIdle]
  );

  /** How many of them delivered something - the divisor of the average row. */
  const deliveringCount = series.length - idleCount;

  /**
   * The first foot row: the sum of the rows on screen, month by month and period by period.
   *
   * Of `visible`, so that it is the sum of what is displayed, as asked. The figures do not in
   * fact move when the switch is thrown - a hidden row contributes nothing but zeros - but the
   * count beside the label does, and a total that claimed more rows than the table holds would
   * be wrong about the one thing it is there to say.
   */
  const sum = React.useMemo(() => combineSeries(visible, SUM_LABEL), [visible]);

  /**
   * The second foot row: that total per instructor who delivered.
   *
   * Taken from `series` rather than from `visible`, and divided by `deliveringCount` rather than
   * by the rows on screen, so that neither half of the fraction can move when the switch is
   * thrown. See perInstructorAverage for why the idle are excluded from the division at all.
   *
   * Null when nobody delivered: 0 / 0 is not zero, and a row of zeros labelled "Average" would
   * state something the data does not.
   */
  const average = React.useMemo(() => (deliveringCount > 0
    ? perInstructorAverage(combineSeries(series, SUM_LABEL), deliveringCount, 'Average')
    : null), [series, deliveringCount]);

  const monthKeys = React.useMemo(
    () => fiscalYearMonths(applied?.fiscalYear ?? currentFiscalYear),
    [applied, currentFiscalYear]
  );

  /** The fiscal year the table is reporting on, as the Year column names it. */
  const yearLabel = React.useMemo(
    () => fiscalYearLabel(applied?.fiscalYear ?? currentFiscalYear),
    [applied, currentFiscalYear]
  );

  // --- Toolbar --------------------------------------------------------------------------

  const onDeleteFilter = (category: string | ToolbarLabelGroup, label: string | ToolbarLabel) => {
    const name = String(typeof category === 'string' ? category : category.name);
    const field: keyof UtilizationCriteria | undefined =
      name === 'Instructor' ? 'instructorId' : name === 'Partner' ? 'partnerId' : undefined;
    if (!field) return;

    const value = typeof label === 'string' ? label : String(label.key);
    setDraft(current => ({ ...current, [field]: (current[field] as string[]).filter(entry => entry !== value) }));
  };

  /** Empties the pickers. The fiscal year is not a filter - there is always exactly one. */
  const clearFilters = () => setDraft(current => criteriaFor(current.fiscalYear));

  const idLabels = <T extends { id: number }>(ids: string[], items: T[], label: (item: T) => string) =>
    ids.map(id => {
      // A dictionary still loading, or a record since removed, leaves the id readable rather
      // than showing an empty chip.
      const item = items.find(entry => String(entry.id) === id);
      return { key: id, node: item ? label(item) : `#${id}` };
    });

  const buildYearSelect = () => (
    <ToolbarItem>
      <Select
        aria-label="Fiscal year" isOpen={isYearOpen} onOpenChange={setIsYearOpen}
        selected={String(draft.fiscalYear)}
        onSelect={(_event, value) => { setDraft(current => ({ ...current, fiscalYear: Number(value) })); setIsYearOpen(false); }}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsYearOpen(!isYearOpen)} isExpanded={isYearOpen}
            style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            FY {fiscalYearLabel(draft.fiscalYear)}
          </MenuToggle>
        )}
      >
        {/* Newest last, as the business writes them. Apr-Mar, so the year named 25/26 begins in
            April 2025 - the toggle says FY to leave no doubt it is not a calendar year. */}
        {years.map(year => (
          <SelectOption key={year} value={String(year)} isSelected={year === draft.fiscalYear}>
            FY {fiscalYearLabel(year)} ({`Apr ${year} - Mar ${year + 1}`})
          </SelectOption>
        ))}
      </Select>
    </ToolbarItem>
  );

  /** A searchable checkbox Select over one of the dictionaries, sending back ids. */
  const buildOptionFilter = <T extends { id: number }>(
    category: string,
    field: 'instructorId' | 'partnerId',
    items: T[],
    label: (item: T) => string
  ) => {
    const matching = items.filter(item => label(item).toLowerCase().includes(optionSearch.toLowerCase()));

    return (
      <ToolbarFilter
        labels={idLabels(draft[field], items, label)}
        deleteLabel={onDeleteFilter}
        categoryName={category}
      >
        <Select
          aria-label={category} isOpen={isFilterOpen(category)}
          onOpenChange={(isOpen) => setOpenFilter(isOpen ? category : null)}
          popperProps={{ minWidth: '260px' }}
          selected={draft[field]}
          onSelect={(_event, value) => setDraft(current => {
            const held = current[field];
            const picked = String(value);
            return {
              ...current,
              [field]: held.includes(picked) ? held.filter(entry => entry !== picked) : [...held, picked]
            };
          })}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle ref={toggleRef} onClick={() => toggleFilter(category)} isExpanded={isFilterOpen(category)}
              style={{ width: '100%', verticalAlign: 'text-bottom' }}>
              {`Filter by ${category.toLowerCase()}`}
              {draft[field].length > 0 && <> <Badge isRead>{draft[field].length}</Badge></>}
            </MenuToggle>
          )}
        >
          <div style={{ padding: '8px' }}>
            <SearchInput ref={isFilterOpen(category) ? focusOnOpen : undefined}
              value={optionSearch} onChange={(_event, value) => setOptionSearch(value)}
              onClear={() => setOptionSearch('')} placeholder={`Search ${category.toLowerCase()}...`}
              aria-label={`Search ${category}`} />
          </div>
          <Divider />
          {matching.map(item => (
            <SelectOption hasCheckbox key={item.id} value={String(item.id)}
              isSelected={draft[field].includes(String(item.id))}>
              {label(item)}
            </SelectOption>
          ))}
          {matching.length === 0 && <SelectOption isDisabled value="no-results">No results found</SelectOption>}
        </Select>
      </ToolbarFilter>
    );
  };

  const activeFilterCount = draft.instructorId.length + draft.partnerId.length;

  // --- Export ------------------------------------------------------------------------------

  /**
   * What is on screen: one row per instructor, and the foot row's sum under them. Rows hidden by
   * the switch are left out of the file too - the export is of the table, not of the query.
   */
  const exportAll = (exportFormat: ExportFormat) => {
    if (!applied || visible.length === 0) {
      addNotification('Nothing to export', AlertVariant.warning, 'No delivery was reported for the current filters.');
      return;
    }

    try {
      const subject = average ? [...visible, sum, average] : [...visible, sum];
      const fileName = exportRecords(
        utilizationExportRows(subject, applied.fiscalYear),
        `instructor_utilization_fy${fiscalYearLabel(applied.fiscalYear).replace('/', '-')}`,
        exportFormat,
        'Utilization'
      );
      addNotification('Export ready', AlertVariant.success,
        `${subject.length} row${subject.length === 1 ? '' : 's'} exported to ${fileName}`);
    } catch (failure) {
      addNotification('Error exporting utilization', AlertVariant.danger, toApiError(failure).message);
    }
  };

  const kebabItems: PageActionsMenuItem[] = [
    { key: 'export-csv', label: 'Export to CSV...', onClick: () => exportAll('csv'), isDisabled: visible.length === 0 },
    { key: 'export-xlsx', label: 'Export to Excel...', onClick: () => exportAll('xlsx'), isDisabled: visible.length === 0 }
  ];

  // --- Cells ----------------------------------------------------------------------------

  /**
   * A figure, with the days credited over a whole day named where there are any. The service
   * reports them rather than correcting them: 1.5 on a day is a full class plus a three hour
   * sitting, and 2.0 is usually an instructor recorded on two classes at once.
   */
  const daysCell = (value: number, overAllocated: string[], isStrong = false) => {
    const figure = isStrong ? <strong>{formatDays(value)}</strong> : formatDays(value);

    if (overAllocated.length === 0) {
      // A zero is faded so the months that carry something stand out of a table that is mostly
      // zeros - except in the tinted columns, where the theme's subtle grey on a pale orange
      // fails contrast and app.css pins the text dark instead.
      return value === 0 ? <span className="cas-utilization-zero">{figure}</span> : figure;
    }

    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        {figure}{' '}
        <Tooltip content={
          <div>
            Credited more than a whole day on{' '}
            {overAllocated.map(date => ddMMyyyyFormat(fromIsoDate(date))).join(', ')}.
            A day and a half is a class plus an RHLS sitting; two days is usually an instructor
            recorded on two classes at once.
          </div>
        }>
          <Icon status="warning" isInline><ExclamationTriangleIcon /></Icon>
        </Tooltip>
      </span>
    );
  };

  /**
   * One row of the table: an instructor, or one of the two foot rows.
   *
   * `label` is passed rather than read off the series because the foot rows say more than their
   * name - how many instructors they cover, and in the average's case what it divided by, which
   * is the one thing a reader has to know to quote the figure.
   */
  const renderRow = (
    rowKey: string,
    one: UtilizationSeries,
    label: React.ReactNode,
    foot?: { isFirst: boolean }
  ) => (
    <Tr key={rowKey}
      className={foot ? `cas-utilization-sum${foot.isFirst ? ' cas-utilization-sum-top' : ''}` : undefined}
      // The foot rows are not anybody's row to mark, and a total that highlights on a stray
      // click reads as though it had been filtered.
      isSelectable={!foot} isClickable={!foot}
      isRowSelected={!foot && one.instructorId !== null && selectedRowId === one.instructorId}
      onRowClick={foot ? undefined : () => setSelectedRowId(
        current => (current === one.instructorId ? null : one.instructorId)
      )}>
      {/* hasRightBorder is what makes the frozen column read as frozen: it does not move
          already, but without a rule against the scrolling columns a name simply appears to
          sit beside whichever month has been scrolled next to it. */}
      <Td dataLabel="Instructor" isStickyColumn stickyMinWidth="200px" stickyLeftOffset="0"
        hasRightBorder modifier="truncate">
        {label}
      </Td>

      {FISCAL_PERIOD_ROWS.map(period => {
        const { total, average: periodAverage } = periodFigures(one.days, period);

        return (
          <React.Fragment key={period.key}>
            {period.kind === 'quarter' && period.months.map(index => (
              <Td key={index} dataLabel={FISCAL_MONTH_LABELS[index]} textCenter>
                {daysCell(one.days[index] ?? 0, one.overAllocated[index] ?? [], !!foot)}
              </Td>
            ))}
            <Td dataLabel={`${period.label} total`} textCenter className="cas-utilization-total">
              {daysCell(total, periodOverAllocated(one.overAllocated, period), true)}
            </Td>
            <Td dataLabel={`${period.label} average`} textCenter hasRightBorder
              className="cas-utilization-average">
              {formatDays(Number(periodAverage.toFixed(1)))}
            </Td>
          </React.Fragment>
        );
      })}
    </Tr>
  );

  const renderTable = () => (
    <Table aria-label="Utilization by instructor" variant="compact" gridBreakPoint=""
      className="cas-wide-table" isStickyHeader>
      {/* `hasNestedHeader` tells PatternFly these two rows are one header, which is what gets the
          group row its own padding and the rule between the two. Kept from the attempt to chase
          the Firefox seam, none of which worked: this part is simply the right prop for a header
          built this way, and is not a workaround for anything. */}
      <Thead hasNestedHeader>
        {/* Two header rows: the periods above, their columns below. One row of 27 headers would
            not say which Total belongs to which quarter. */}
        <Tr>
          <Th rowSpan={2} isStickyColumn stickyMinWidth="200px" stickyLeftOffset="0"
            hasRightBorder>Instructor</Th>
          {FISCAL_PERIOD_ROWS.map(period => {
            // A quarter says which months it holds, because the subheader row below names them
            // one by one and the pair reads as a heading. A half says nothing beyond H1 or H2:
            // naming its quarters only repeated the two column groups immediately to its left.
            // The year carries the fiscal year itself, which is the one thing on this table that
            // the table cannot otherwise tell you.
            const qualifier = period.kind === 'quarter' ? periodMonthSpan(period)
              : period.kind === 'year' ? yearLabel
                : null;

            return (
              <Th key={period.key} colSpan={period.kind === 'quarter' ? 5 : 2} textCenter hasRightBorder>
                {period.label}
                {qualifier && (
                  <>
                    {' '}
                    <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontWeight: 'normal' }}>
                      {qualifier}
                    </span>
                  </>
                )}
              </Th>
            );
          })}
        </Tr>
        <Tr>
          {FISCAL_PERIOD_ROWS.map(period => (
            <React.Fragment key={period.key}>
              {period.kind === 'quarter' && period.months.map(index => (
                <Th key={index} isSubheader textCenter>{FISCAL_MONTH_LABELS[index]}</Th>
              ))}
              <Th isSubheader textCenter className="cas-utilization-total">Total</Th>
              <Th isSubheader textCenter hasRightBorder tooltip={AVERAGE_HINT}
                className="cas-utilization-average">Avg</Th>
            </React.Fragment>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {visible.map(one => renderRow(String(one.instructorId), one, one.instructorName))}
        {renderRow('sum', sum, <strong>{SUM_LABEL} ({visible.length})</strong>, { isFirst: true })}
        {average && renderRow('average', average, (
          <Tooltip content={PER_INSTRUCTOR_HINT}>
            <span className="tooltipped-text-bold">Average (&divide;{deliveringCount})</span>
          </Tooltip>
        ), { isFirst: false })}
      </Tbody>
    </Table>
  );

  // --- Render ----------------------------------------------------------------------------------

  const renderBody = () => {
    if (error) return <DataLoadError error={error} entityLabel="utilization" onRetry={refresh} />;

    // Nothing has been asked for yet. The page deliberately fetches nothing on arrival.
    if (!applied) {
      return (
        <Bullseye>
          <EmptyState headingLevel="h5" icon={SearchIcon} titleText="Choose a fiscal year and search">
            <EmptyStateBody>
              Pick the fiscal year, and the instructors or partners to report on, then press
              Search. Leaving both pickers empty reports on every instructor who delivered.
            </EmptyStateBody>
          </EmptyState>
        </Bullseye>
      );
    }

    // A search is on its way: the app's own backdrop is over the page, so anything drawn here
    // would only flash past the reader.
    if (!hasAnswer) return null;

    if (subjects.isEmpty) {
      return (
        <Bullseye>
          <EmptyState headingLevel="h5" icon={UsersIcon} titleText="The filter matches no instructor">
            <EmptyStateBody>
              The partners selected have no instructors on them, or the instructors named no longer
              exist. Change the filters and search again.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="link" onClick={clearFilters}>Clear all filters</Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </Bullseye>
      );
    }

    // Every row there was is an idle one, and the switch is hiding them. Distinguished from the
    // state below: there is something to show here, the reader has asked not to see it.
    if (visible.length === 0 && series.length > 0) {
      return (
        <Bullseye>
          <EmptyState headingLevel="h5" icon={UsersIcon} titleText="Every instructor here delivered nothing">
            <EmptyStateBody>
              All {series.length} instructor{series.length === 1 ? '' : 's'} in this selection have
              a year of zeros, and the switch is hiding them.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="link" onClick={() => setHideIdle(false)}>Show them anyway</Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </Bullseye>
      );
    }

    if (series.length === 0) {
      return (
        <Bullseye>
          <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No delivery in this fiscal year">
            <EmptyStateBody>
              Nothing was delivered between April {applied.fiscalYear} and March {applied.fiscalYear + 1}.
              Pick another fiscal year, or widen the filters.
            </EmptyStateBody>
          </EmptyState>
        </Bullseye>
      );
    }

    return (
      <React.Fragment>
        <div style={{
          padding: 'var(--pf-t--global--spacer--sm) var(--pf-t--global--spacer--md)',
          color: 'var(--pf-t--global--text--color--subtle)',
          fontSize: 'var(--pf-t--global--font--size--sm)'
        }}>
          FY {fiscalYearLabel(applied.fiscalYear)}, {monthKeys[0]} to {monthKeys[11]} &middot;{' '}
          {visible.length} instructor{visible.length === 1 ? '' : 's'}
          {/* Said either way, so that the zero rows are never silently absent and never merely
              noise: the count is the same number, read as a hint or as a receipt. */}
          {idleCount > 0 && (hideIdle
            ? ` (${idleCount} with no delivery hidden)`
            : ` (${idleCount} with no delivery)`)}
          {!subjects.named && ' \u00b7 every instructor who delivered'}
          {' '}&middot; days of delivery
        </div>
        <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
          <InnerScrollContainer>{renderTable()}</InnerScrollContainer>
        </OuterScrollContainer>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button variant="plain" aria-label="Toggle filters" style={{ padding: 0, marginTop: '4px' }}
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}>
                <Icon size="xl">{isFiltersExpanded ? <AngleDownIcon /> : <AngleRightIcon />}</Icon>
              </Button>
              <Title headingLevel="h1" size="4xl">Utilization</Title>
              {!isFiltersExpanded && activeFilterCount > 0 && (
                <Badge isRead>{activeFilterCount} Active Filter{activeFilterCount !== 1 ? 's' : ''}</Badge>
              )}
            </div>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <PageActionsMenu onRefresh={refresh} items={kebabItems} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {isFiltersExpanded && (
        <Toolbar id="utilization-toolbar" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
          <ToolbarContent>
            <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
              <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
                {buildYearSelect()}
                {buildOptionFilter('Instructor', 'instructorId', instructors, instructor => instructor.name)}
                {buildOptionFilter('Partner', 'partnerId', partners, partner => partner.name)}
              </ToolbarGroup>
            </ToolbarToggleGroup>
            {/* Outside the toggle group on purpose: below xl that group folds into a dropdown,
                and the button that runs the search has to stay in reach. */}
            <ToolbarItem>
              <Button variant="primary" icon={<SearchIcon />} onClick={search}>Search</Button>
            </ToolbarItem>
            {/* Outside the filter group and not part of the criteria: it changes what the table
                shows, not what was asked for, so it takes effect without a new search. */}
            <ToolbarItem align={{ default: 'alignEnd' }}>
              <Switch id="hide-idle-switch" label="Hide instructors with no delivery"
                isChecked={hideIdle} onChange={(_event, checked) => setHideIdle(checked)} />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
      )}

      {renderBody()}
    </React.Fragment>
  );
};

export { Utilization };
